// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { observable, computed, action, makeObservable, runInAction } from 'mobx';
import { injectable } from 'inversify';

import Platform, {
  PlatformDTO,
  DroneManufacturer,
  DroneModel,
  CameraItem,
  GimbalItem,
  LenseItem,
  GPSProvider
} from '@shared/models/platform';
import container from '@core/di';
import PlatformsService from '@shared/services/platforms';
import BaseStore from '@core/stores/base';

@injectable()
export default class PlatformsStore extends BaseStore<PlatformDTO, Platform, PlatformsService> {
  static diToken = Symbol('platforms-store');
  @observable _droneManufacturers: Array<DroneManufacturer> = [];
  @observable _droneModels: Array<DroneModel> = [];
  @observable _cameras: Array<CameraItem> = [];
  @observable _gimbals: Array<GimbalItem> = [];
  @observable _lenses: Array<LenseItem> = [];
  @observable _gpsProviders: Array<GPSProvider> = [];

  constructor() {
    super({ service: container.get<PlatformsService>(PlatformsService.diToken), });

    this.loading = {
      customers: false,
      droneModels: false,
      droneManufacturers: false,
      cameras: false,
      gimbals: false,
      lenses: false,
      gpsProvidersId: false,
      list: false,
      item: false,
    };
    makeObservable(this);
  }

  @computed get droneManufacturers() {
    return this._droneManufacturers;
  }

  @computed get droneModels() {
    return this._droneModels;
  }

  @computed get cameras() {
    return this._cameras;
  }

  @computed get gimbals() {
    return this._gimbals;
  }

  @computed get lenses() {
    return this._lenses;
  }

  @computed get gpsProviders() {
    return this._gpsProviders;
  }

  deactivatePlatform(id: PlatformDTO['id'], isBlocked: PlatformDTO['isBlocked']) {
    return this.service.deactivatePlatform(id, isBlocked);
  }

  @action getDroneModels = async (id: DroneManufacturer['id']) => {
    this.loading.droneModels = true;

    try {
      const droneModels = await this.service.getDroneModels(id);
      runInAction(() => {
        this._droneModels = droneModels;
        this.loading.droneModels = false;
      });
    } catch {
      runInAction(() => {
        this.loading.droneModels = false;
      });
    }
  }

  @action getDroneManufacturers = async () => {
    this.loading.droneManufacturers = true;

    try {
      const droneManufacturers = await this.service.getDroneManufacturers();
      runInAction(() => {
        this._droneManufacturers = droneManufacturers;
        this.loading.droneManufacturers = false;
      });
    } catch {
      runInAction(() => {
        this.loading.droneManufacturers = false;
      });
    }
  }

  @action getCameras = async () => {
    this.loading.cameras = true;

    try {
      const cameras = await this.service.getCameras();
      runInAction(() => {
        this._cameras = cameras;
        this.loading.cameras = false;
      });
    } catch {
      runInAction(() => {
        this.loading.cameras = false;
      });
    }
  }

  @action getGimbals = async () => {
    this.loading.gimbals = true;

    try {
      const gimbals = await this.service.getGimbals();
      runInAction(() => {
        this._gimbals = gimbals;
        this.loading.gimbals = false;
      });      
    } catch {
      runInAction(() => {
        this.loading.gimbals = false;
      });
    }
  }

  @action getLenses = async () => {
    this.loading.lenses = true;

    try {
      const lenses = await this.service.getLenses();
      runInAction(() => {
        this._lenses = lenses;
        this.loading.lenses = false;
      });
    } catch {
      runInAction(() => {
        this.loading.lenses = false;
      });
    }
  }

  @action getGpsProviders = async () => {
    this.loading.gpsProvidersId = true;

    try {
      const gpsProviders = await this.service.getGpsProviders();
      runInAction(() => {
        this._gpsProviders = gpsProviders;
        this.loading.gpsProvidersId = false;
      });
    } catch {
      runInAction(() => {
        this.loading.gpsProvidersId = false;
      });
    }
  }
}
